import React from 'react';
import { Tooltip } from 'antd';

// Assets
import Delivered from '../../../../assets/customSVG/inbox/Delivered';
import Sent from '../../../../assets/customSVG/inbox/Sent';
import { ICONS } from '../../../../assets/icons';

// Components
import ReplyMessageBar from './ReplyMessageBar';

// Helpers
import { dateConverter } from '../../../../helper/dateConverter';
import { addLineBreake, replaceFormating } from '../../helper';
import { getRandomColorsBassedOnLetters } from '../../../../helper/colors';

// Components
import MediaMessage from '../../../campaigns/components/sequence/preview/MediaMessage';
import ConversationCard from './ConversationCard';
import DocumentMessage from './DocumentMessage';
import ImageMessagePreview from './ImageMessagePreview';
import VideoMessagePreview from './VideoMessagePreview';
import LocationMessage from '../../../../components/common/LocationMessage';
import OrderDetailTemplate from '../../../../components/common/OrderDetailTemplate';
import OrderStatusTemplate from '../../../../components/common/OrderStatusTemplate';
import AudioMessagePreview from './AudioMessagePreview';

const MESSAGE_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  LOCATION: 'location',
  CONTACTS: 'contacts',
  INTERACTIVE: 'interactive',
  NOTE: 'note',
  WINDOW: 'window',
  NOTE_ATTACHMENT: 'note_attachment',
  TEMPLATE: 'template',
};

const INTERACTIVE_TYPES = {
  BUTTON: 'button',
  LIST: 'list',
  LOCATION_REQUEST: 'location_request_message',
  CTA_URL: 'cta_url',
  FLOW: 'flow',
};

const BUTTON_TYPES = {
  URL: 'URL',
  PHONE_NUMBER: 'PHONE_NUMBER',
  COPY_CODE: 'COPY_CODE',
  QUICK_REPLY: 'QUICK_REPLY',
};

const STATUS_TYPES = {
  READ: 'READ',
  REPLIED: 'REPLIED',
  CLICKED: 'CLICKED',
  DELIVERED: 'DELIVERED',
  SENT: 'SENT',
  BOUNCED: 'BOUNCED',
};

const RightSideConversationCard = ({
  data,
  handleReply,
  handleCopy,
  isChatResolved,
  getData,
}) => {
  const timestamp = dateConverter(data?.created_at, false);

  // Helper Functions
  const parseMessageObject = (obj) => {
    return typeof obj === 'string' ? JSON.parse(obj) : '';
  };

  const getCarouselData = () => {
    const carouselData = data?.message?.template?.filter(
      (item) => item?.type === 'CAROUSEL' && item?.cards,
    );

    if (!carouselData?.[0]?.cards) return null;

    const cards = carouselData[0].cards;

    return {
      headers: cards.map((card) =>
        card?.components?.find((component) => component?.type === 'HEADER'),
      ),
      bodies: cards.map((card) =>
        card?.components?.find((component) => component?.type === 'BODY'),
      ),
      buttonsList: cards.map(
        (card) =>
          card?.components?.find((component) => component?.type === 'BUTTONS')
            ?.buttons,
      ),
      cards,
    };
  };

  const getMessageBackgroundColor = () => {
    return data?.message?.type === MESSAGE_TYPES.NOTE
      ? 'bg-[#FFEC9A]'
      : 'bg-[#D8FBD2]';
  };

  const shouldShowMessage = () => {
    return (
      data?.message?.type !== MESSAGE_TYPES.WINDOW &&
      data?.message?.type !== MESSAGE_TYPES.NOTE_ATTACHMENT
    );
  };

  // Component Renderers
  const renderActionButtons = () => {
    if (isChatResolved) return null;

    return (
      <div className="action-icons-right pr-2">
        <img
          src={ICONS?.replyIcon}
          onClick={() => handleReply(data?.id)}
          className="pointer w-[18px] h-[18px]"
          alt="Reply"
        />
      </div>
    );
  };

  const renderStatusIcon = () => {
    const { status } = data;
    if (
      [
        STATUS_TYPES.READ,
        STATUS_TYPES.REPLIED,
        STATUS_TYPES.CLICKED,
        STATUS_TYPES.DELIVERED,
      ].includes(status)
    ) {
      return (
        <Delivered
          color={
            status === STATUS_TYPES.DELIVERED ? 'var(--font-600)' : '#1778F2'
          }
        />
      );
    }

    if (status === STATUS_TYPES.SENT) {
      return <Sent color="var(--font-600)" />;
    }

    return null;
  };

  const renderBounceError = () => {
    if (data?.status !== STATUS_TYPES.BOUNCED) return null;
    if ([MESSAGE_TYPES.TEMPLATE].includes(data?.message?.type)) return null;

    return (
      <Tooltip title={JSON.stringify(data?.error_notes)}>
        <div className="flex-row align-center justify-end gap-1 text-[0.8rem] cursor-pointer font-[500] text-[#FF543E] ptb-5">
          <img src={ICONS?.inboxBounced} alt="Bounced" />
          Unable to send the message!
        </div>
      </Tooltip>
    );
  };

  const renderTimestampAndStatus = () => (
    <div className="flex justify-end text-[0.55vw] gap-1.5 weight-semibold text-[var(--font-400)] mt-[0.3vw] ml-40">
      <p>{timestamp?.timeWithMeridiem}</p>
      {renderStatusIcon()}
    </div>
  );

  const renderCarouselButton = (button, btnIndex) => {
    const buttonStyle =
      'flex items-center gap-[0.5vw] h-[2vw] text-[#007AFF] text-[0.875rem] font-[500]';

    const buttonConfig = {
      [BUTTON_TYPES.URL]: {
        icon: ICONS?.nounLink,
        text: button?.text,
        className: 'size-[0.9vw]',
      },
      [BUTTON_TYPES.PHONE_NUMBER]: {
        icon: ICONS?.call,
        text: button?.text,
        className: 'size-[0.95vw]',
      },
      [BUTTON_TYPES.COPY_CODE]: {
        icon: ICONS?.CopyLeft,
        text: 'Copy Offer Code',
        className: 'size-[0.95vw]',
      },
      [BUTTON_TYPES.QUICK_REPLY]: {
        icon: ICONS?.shareLink,
        text: button?.text,
        className: 'size-[0.8vw]',
      },
    };

    const config = buttonConfig[button?.type];
    if (!config) return null;

    const ButtonComponent =
      button?.type === BUTTON_TYPES.QUICK_REPLY ? 'div' : 'button';

    return (
      <div
        key={btnIndex}
        className="text-[0.875rem] font-[500] flex items-center justify-center border-b border-[var(--border-50)] last:border-none"
      >
        <ButtonComponent className={buttonStyle}>
          <img
            src={config.icon}
            alt={`${button?.type} icon`}
            className={config.className}
          />
          {button?.type === BUTTON_TYPES.QUICK_REPLY ? (
            <p>{config.text}</p>
          ) : (
            config.text
          )}
        </ButtonComponent>
      </div>
    );
  };

  const renderCarouselCard = (
    card,
    cardIndex,
    { headers, bodies, buttonsList },
  ) => (
    <div
      key={cardIndex}
      className="w-[18vw] h-auto flex flex-col justify-between mt-10 bg-[#D8FBD2] rounded-[0.6vw] rounded-tr-none p-[0.8vw]"
    >
      {/* Header Media */}
      {headers[cardIndex]?.format === 'IMAGE' && (
        <img
          src={headers[cardIndex]?.example?.header_handle}
          alt={`Header Image ${cardIndex}`}
          className="w-full h-[160px] object-cover rounded-[0.4vw]"
        />
      )}

      {headers[cardIndex]?.format === 'VIDEO' && (
        <video
          className="object-cover h-[150px] w-full rounded-[0.4vw]"
          controls
          autoPlay
        >
          <source
            src={headers[cardIndex]?.example?.header_handle}
            type="video/mp4"
          />
        </video>
      )}

      {/* Body Content */}
      {bodies[cardIndex]?.text && (
        <div className="new-body-text mt-2">{bodies[cardIndex].text}</div>
      )}

      {/* Action Buttons */}
      <div className="button-group mt-4 flex flex-col gap-2">
        {buttonsList[cardIndex]?.map((button, btnIndex) =>
          renderCarouselButton(button, btnIndex),
        )}
      </div>
    </div>
  );

  const renderUserAvatar = () => (
    <div className="w-7 pt-5 pointer">
      <Tooltip title={data?.user_name}>
        {data?.user_profile_photo ? (
          <div className="w-100">
            <img
              src={data?.user_profile_photo}
              alt={data?.user_name || 'User'}
              className="rounded-full w-[80%] ml-5"
            />
          </div>
        ) : (
          data?.user_name && (
            <span
              className="w-[1.65rem] h-[1.65rem] rounded-full border-[2px] border-white text-[0.65rem] font-[600] flex items-center justify-center"
              style={{
                color: 'white',
                backgroundColor: getRandomColorsBassedOnLetters(
                  data?.user_name?.slice(0, 1).toUpperCase(),
                  1.5,
                ).backgroundColor,
              }}
            >
              {data?.user_name?.slice(0, 1).toUpperCase()}
            </span>
          )
        )}
      </Tooltip>
    </div>
  );

  const extractReplyContent = (replyMessage) => {
    if (!replyMessage) return { text: null, items: null };

    const text = replyMessage.template?.map((item) => item?.text);
    const items = replyMessage.template?.map((item) => item);

    return { text, items };
  };

  const renderMessageContent = () => {
    const { message } = data;
    const { text: reply_message, items: previewContent } = extractReplyContent(
      data?.reply_message,
    );

    // Handle different message types
    switch (message?.type) {
      case MESSAGE_TYPES.LOCATION:
        return (
          <>
            <ReplyMessageBar
              replyMessage={data?.reply_message}
              replyText={data?.reply_message?.text?.body}
            />
            <LocationMessage
              location={message.location}
              timestamp={timestamp?.timeWithMeridiem}
              isRightSide={true}
              className="ml-auto"
            />
          </>
        );

      case MESSAGE_TYPES.IMAGE:
        return (
          <>
            <ReplyMessageBar
              replyMessage={data?.reply_message}
              replyText={data?.reply_message?.text?.body}
            />
            <ImageMessagePreview
              content={reply_message}
              urls={previewContent
                ?.map(
                  (item) =>
                    item?.format === 'IMAGE' &&
                    item?.example?.header_handle?.[0],
                )
                .filter(Boolean)}
              url={message?.image?.link}
              caption={message?.image?.caption}
            />
          </>
        );

      case MESSAGE_TYPES.VIDEO:
        return (
          <>
            <ReplyMessageBar
              replyMessage={data?.reply_message}
              replyText={data?.reply_message?.text?.body}
            />
            <VideoMessagePreview
              content={reply_message}
              urls={previewContent
                ?.map(
                  (item) =>
                    item?.format === 'VIDEO' &&
                    item?.example?.header_handle?.[0],
                )
                .filter(Boolean)}
              url={message?.video?.link}
              caption={message?.video?.caption}
            />
          </>
        );

      case MESSAGE_TYPES.DOCUMENT:
        return (
          <>
            <ReplyMessageBar
              replyMessage={data?.reply_message}
              replyText={data?.reply_message?.text?.body}
            />
            <DocumentMessage
              fileName={message?.document?.filename}
              fileSize={message?.document?.fileLength}
              fileType={message?.document?.type}
              fileUrl={message?.document?.link}
              fileCaption={message?.document?.caption}
              isOwn={data?.is_customer_msg}
            />
          </>
        );

      case MESSAGE_TYPES.AUDIO:
        return (
          <>
            <ReplyMessageBar
              replyMessage={data?.reply_message}
              replyText={data?.reply_message?.text?.body}
            />
            <AudioMessagePreview
              url={message?.audio?.link}
              caption={message?.audio?.caption}
              className="max-w-[26vw]"
              rootClassName="bg-transparent p-0"
            />
          </>
        );

      case MESSAGE_TYPES.CONTACTS:
        return (
          <>
            <ReplyMessageBar
              replyMessage={data?.reply_message}
              replyText={data?.reply_message?.text?.body}
            />
            <MediaMessage
              previewMode={true}
              node={{
                type: message?.interactive?.type?.toUpperCase() ?? 'CONTACTS',
                data: message,
              }}
              allowToShowEdge={false}
              rootClassName="bg-transparent"
              getData={getData}
            />
          </>
        );

      default:
        // Handle text messages and complex message structures
        if (data?.message?.subcategory == 'ORDER_DETAIL') {
          return (
            <OrderDetailTemplate
              data={data}
              message={data?.message}
              settings={data?.settings}
            />
          );
        } else if (data?.message?.subcategory == 'ORDER_STATUS') {
          return (
            <OrderStatusTemplate
              data={data}
              message={data?.message}
              settings={data?.settings}
            />
          );
        } else if (
          data?.is_customer_msg === false &&
          Array.isArray(message?.template || message)
        ) {
          return (
            <>
              <ReplyMessageBar
                replyMessage={data?.reply_message}
                replyText={data?.reply_message?.text?.body}
              />
              <ConversationCard data={data} />
            </>
          );
        }

        const parsedMessage = parseMessageObject(message);
        const messageText =
          parsedMessage?.text?.body ||
          parsedMessage?.body ||
          message?.text?.body ||
          message?.body ||
          message?.text;

        if (messageText) {
          return (
            <>
              <ReplyMessageBar
                replyMessage={data?.reply_message}
                replyText={data?.reply_message?.text?.body}
              />
              <div
                className="w-full break-words new-body-text inbox-formatted-message"
                dangerouslySetInnerHTML={{
                  __html: addLineBreake(replaceFormating(messageText)),
                }}
              />
            </>
          );
        }

        return null;
    }
  };

  const renderInteractiveMessage = () => {
    const { message } = data;
    const interactiveType = message?.interactive?.type;

    if (!Object.values(INTERACTIVE_TYPES).includes(interactiveType)) {
      return null;
    }

    return (
      <div className="w-fit min-w-[15vw] max-w-[20.5vw] min-h-[3.5vh]">
        <MediaMessage
          previewMode={true}
          node={{
            type: interactiveType?.toUpperCase() ?? 'LIST',
            data: message,
          }}
          allowToShowEdge={false}
          rootClassName="bg-transparent"
          getData={getData}
        />
        {renderBounceError()}
        {renderTimestampAndStatus()}
      </div>
    );
  };

  const renderRegularMessage = () => (
    <div className="max-w-[20.5vw] min-h-[3.5vh]">
      {renderMessageContent()}
      {renderBounceError()}
      {renderTimestampAndStatus()}
    </div>
  );

  const renderCarouselSection = () => {
    const carouselData = getCarouselData();

    if (!carouselData) return null;

    return (
      <div className="w-100 flex">
        <div className="flex align-center opacity-0">
          <img
            src={ICONS?.replyIcon}
            onClick={() => handleReply(data?.id)}
            className="pointer pr-2 w-[23px] h-[23px]"
            alt="Reply"
          />
        </div>
        <div className="min-h-[4vw] max-w-[20vw] flex gap-2.5 overflow-x-scroll listScroll1 pb-2">
          <div className="min-h-[13rem] listScroll1 flex gap-4">
            {carouselData.cards.map((card, cardIndex) =>
              renderCarouselCard(card, cardIndex, carouselData),
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main render
  if (!shouldShowMessage()) {
    return null;
  }

  const isInteractiveMessage =
    data?.message?.type === MESSAGE_TYPES.INTERACTIVE;

  return (
    <div className="">
      <div className="single-conversation-container flex-row align-center justify-center">
        {renderActionButtons()}

        <div className="w-100 flex-column justify-end items-end">
          <div
            className={`relative w-full rounded-[0.6vw] p-[0.8vw] ${getMessageBackgroundColor()}`}
          >
            {isInteractiveMessage
              ? renderInteractiveMessage()
              : renderRegularMessage()}
          </div>
          {renderUserAvatar()}
        </div>
      </div>

      {renderCarouselSection()}
    </div>
  );
};

export default RightSideConversationCard;
